import { createServerFn } from '@tanstack/react-start';
import DOMPurify from 'dompurify';
import { JSDOM } from 'jsdom';

// Some destination content comes back from the CMS multiply HTML-entity-encoded
// (e.g. "&amp;lt;p&amp;gt;...&amp;lt;/p&amp;gt;" instead of "<p>...</p>"). A single
// DOMPurify pass only unwraps one layer, leaving literal "&nbsp;"/"</p>" text
// visible on the page. Decode repeatedly until the string stops changing —
// using a <textarea> so entities resolve without ever interpreting "<"/">" as
// real tags — then hand the fully-decoded HTML to DOMPurify to sanitize.
const decodeEntities = (window, html) => {
    const textarea = window.document.createElement('textarea');
    let previous = html;
    for (let i = 0; i < 5; i += 1) {
        textarea.innerHTML = previous;
        const decoded = textarea.value;
        if (decoded === previous) break;
        previous = decoded;
    }
    return previous;
};

// The CMS content often uses a non-breaking space (the literal U+00A0
// character, or the "&nbsp;" entity) between every word instead of a normal
// space, which stops the browser from wrapping anywhere in the sentence at
// all. Swap them for regular spaces so text wraps normally.
const NBSP_PATTERN = new RegExp('&nbsp;| ', 'g');
const replaceNonBreakingSpaces = (html) => html.replace(NBSP_PATTERN, ' ');

export const sanitizeHtml = createServerFn({ method: 'POST' })
    .handler(async ({ data }) => {
        const { html } = data
        const window = new JSDOM('').window;
        const purify = DOMPurify(window);
        const decoded = decodeEntities(window, html || '');
        // Content pasted into the CMS from Word/Google Docs often carries
        // inline styles (background-color highlights, stray fonts/colors)
        // that clash with the site's own typography — strip the whole
        // `style` attribute rather than trying to allow-list safe properties.
        const sanitized = purify.sanitize(decoded, { FORBID_ATTR: ['style'] });
        return replaceNonBreakingSpaces(sanitized);
    });
